import React, { useRef } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import "./TechSkills.css";
import {
  FaHtml5,
  FaCss3Alt,
  FaJsSquare,
  FaReact,
  FaGitAlt,
  FaNodeJs,
  FaPython,
  FaDatabase,
} from "react-icons/fa";
import {
  SiTailwindcss,
  SiTypescript,
  SiReactrouter,
  SiRedux,
  SiReactquery,
  SiBootstrap,
  SiNextdotjs,
  SiMysql,
  SiSolidity,
  SiTensorflow,
} from "react-icons/si";

const skills = [
  { icon: <FaHtml5 />, label: "HTML5", color: "#E34F26" },
  { icon: <FaCss3Alt />, label: "CSS3", color: "#1572B6" },
  { icon: <FaJsSquare />, label: "JavaScript", color: "#F7DF1E" },
  { icon: <FaReact />, label: "React", color: "#61DAFB" },
  { icon: <FaGitAlt />, label: "Git", color: "#F05032" },
  { icon: <FaNodeJs />, label: "Node.js", color: "#339933" },
  { icon: <FaPython />, label: "Python", color: "#3776AB" },
  { icon: <FaDatabase />, label: "MongoDB", color: "#47A248" },
  { icon: <SiTailwindcss />, label: "Tailwind CSS", color: "#06B6D4" },
  { icon: <SiTypescript />, label: "Typescript", color: "#3178C6" },
  { icon: <SiReactrouter />, label: "React Native", color: "#61DAFB" },
  { icon: <SiRedux />, label: "Redux", color: "#764ABC" },
  { icon: <SiReactquery />, label: "React Query", color: "#FF4154" },
  { icon: <SiBootstrap />, label: "Bootstrap", color: "#7952B3" },
  { icon: <SiNextdotjs />, label: "Next.js", color: "#764ABC" },
  { icon: <SiMysql />, label: "MySQL", color: "#4479A1" },
  { icon: <SiSolidity />, label: "Solidity", color: "#363636" },
  { icon: <SiTensorflow />, label: "AI Model", color: "#FF6F00" },
];

const TechSkills = () => {
  const constraintsRef = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [15, -15]);
  const rotateY = useTransform(x, [-100, 100], [-15, 15]);

  return (
    <motion.div
      className="skills-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.h1
        className="skills-heading"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        My <span className="tech-stack">Tech Stack</span>
      </motion.h1>

      <motion.div
        className="interactive-background"
        style={{ x, y, rotateX, rotateY, z: 100 }}
        drag
        dragConstraints={constraintsRef}
        dragElastic={0.02}
      >
        {[...Array(30)].map((_, i) => (
          <div key={i} className="floating-particle" />
        ))}
      </motion.div>

      <div className="skills-scene" ref={constraintsRef}>
        <motion.section
          className="skills-list"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          {skills.map((skill, index) => (
            <motion.div
              key={index}
              className="skill-item"
              title={skill.label}
              whileHover={{
                y: -10,
                scale: 1.1,
                boxShadow: `0 15px 30px -5px ${skill.color}80`,
              }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              style={{
                backgroundColor: `${skill.color}15`,
                borderColor: skill.color,
                color: skill.color,
              }}
            >
              <motion.div
                className="skill-icon-container"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{
                  repeat: Infinity,
                  repeatType: "reverse",
                  duration: 5 + index,
                }}
              >
                {skill.icon}
              </motion.div>
              <motion.div
                className="skill-label"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 + index * 0.05 }}
              >
                {skill.label}
              </motion.div>
              <div
                className="skill-pulse"
                style={{ backgroundColor: skill.color }}
              />
            </motion.div>
          ))}
        </motion.section>
      </div>
    </motion.div>
  );
};

export default TechSkills;
